import React from 'react';
import type { BackdatedChoice, BackdatedSubmission } from '../../../services/vendorService';
import { currency } from '../utils/format';

/**
 * M4 (HE Part 2) – the Hired Equipment submissions a backdated rate would reprice, one row per
 * submission. At entry the Operations Coordinator picks "Update to new rate" or "Leave as is" for
 * each; on the approval screen the same table shows the choices read-only.
 */
export interface BackdatedRateChoicesProps {
  submissions: BackdatedSubmission[];
  choices: Record<string, BackdatedChoice | undefined>;
  /** Omit for the read-only (approval) view. */
  onChange?: (formId: string, choice: BackdatedChoice) => void;
  disabled?: boolean;
}

const CHOICE_LABEL: Record<BackdatedChoice, string> = {
  update: 'Update to new rate',
  leave: 'Leave as is',
};

const BackdatedRateChoices: React.FC<BackdatedRateChoicesProps> = ({ submissions, choices, onChange, disabled }) => (
  <div className="tw-overflow-x-auto">
    <table className="tw-w-full tw-text-sm tw-tabular-nums" data-testid="backdated-choices">
      <thead>
        <tr className="tw-border-b tw-border-line tw-text-left tw-text-content-secondary">
          <th className="tw-py-1.5 tw-pr-3 tw-font-semibold">Submission</th>
          <th className="tw-py-1.5 tw-pr-3 tw-font-semibold">Status</th>
          <th className="tw-py-1.5 tw-pr-3 tw-text-right tw-font-semibold">Current Amount</th>
          <th className="tw-py-1.5 tw-pr-3 tw-text-right tw-font-semibold">At New Rate</th>
          <th className="tw-py-1.5 tw-pr-3 tw-text-right tw-font-semibold">Difference</th>
          <th className="tw-py-1.5 tw-font-semibold">Choice</th>
        </tr>
      </thead>
      <tbody>
        {submissions.map((s) => {
          const choice = choices[s.formId];
          return (
            <tr key={s.formId} className="tw-border-b tw-border-line last:tw-border-0">
              <td className="tw-py-1.5 tw-pr-3">
                {s.status === 'Draft' ? (
                  <span>Draft</span>
                ) : (
                  <a href={`/submissions?formId=${encodeURIComponent(s.formId)}`} target="_blank" rel="noreferrer">
                    {s.reference}
                  </a>
                )}
                <span className="tw-ml-1 tw-text-xs tw-text-content-muted">
                  ({s.lineCount} {s.lineCount === 1 ? 'line' : 'lines'})
                </span>
              </td>
              <td className="tw-py-1.5 tw-pr-3">{s.status}</td>
              <td className="tw-py-1.5 tw-pr-3 tw-text-right">{currency(s.currentAmount)}</td>
              <td className="tw-py-1.5 tw-pr-3 tw-text-right">{currency(s.newAmount)}</td>
              <td className={`tw-py-1.5 tw-pr-3 tw-text-right ${s.difference < 0 ? 'tw-text-danger' : ''}`}>
                {s.difference > 0 ? '+' : ''}
                {currency(s.difference)}
              </td>
              <td className="tw-py-1.5">
                {onChange ? (
                  <div role="radiogroup" aria-label={`Choice for ${s.reference}`} className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-1">
                    {(['update', 'leave'] as BackdatedChoice[]).map((value) => (
                      <label key={value} className="tw-inline-flex tw-min-h-[44px] tw-items-center tw-gap-1.5 md:tw-min-h-0">
                        <input
                          type="radio"
                          name={`backdated-${s.formId}`}
                          value={value}
                          checked={choice === value}
                          disabled={disabled}
                          onChange={() => onChange(s.formId, value)}
                        />
                        {CHOICE_LABEL[value]}
                      </label>
                    ))}
                  </div>
                ) : (
                  <span className={choice === 'update' ? 'tw-font-semibold' : ''}>{choice ? CHOICE_LABEL[choice] : '–'}</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default BackdatedRateChoices;
